import * as React from 'react';
import { useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';
import { usePrefetch } from '@/hooks';
import type { SidebarItem } from '@/components/Sidebar';
import { norm, trimSlash } from '@/utils';

type InnerMenuMobileProps = {
	readonly items: SidebarItem[];
	readonly className?: string;
};

const CN = 'c-cnm-inner-menu';
const normalize = (p?: string) => (p ? trimSlash(norm(p)) : '');

export const InnerMenuMobile: React.FC<InnerMenuMobileProps> = ({ items, className }) => {
	const { pathname } = useLocation();
	const { prefetch } = usePrefetch();
	const [open, setOpen] = useState(false);

	const current = normalize(pathname);

	// Sección activa (padre top-level) + sus hijos
	const { sectionLabel, sectionChildren } = useMemo(() => {
		let sectionLabel: string | null = null;
		let sectionChildren: SidebarItem[] = [];

		for (const top of items) {
			const topPath = normalize(top.to);
			const children = top.children ?? [];

			const topMatches = topPath && (current === topPath || current.startsWith(topPath + '/'));
			const anyChildMatches = children.some((ch) => {
				const p = normalize(ch.to);
				if (p && (current === p || current.startsWith(p + '/'))) return true;
				return (ch.children ?? []).some((g) => {
					const gp = normalize(g.to);
					return gp && (current === gp || current.startsWith(gp + '/'));
				});
			});

			if (topMatches || anyChildMatches) {
				sectionLabel = top.label;
				sectionChildren = children;
				break;
			}
		}

		return { sectionLabel, sectionChildren };
	}, [items, current]);

	// Label de la página actual (leaf más profunda)
	const currentLeafLabel = useMemo(() => {
		type LabelLeaf = { path: string; label: string };
		const all: LabelLeaf[] = [];
		const walk = (n?: SidebarItem) => {
			if (!n) return;
			if (n.to && n.label) all.push({ path: normalize(n.to), label: n.label });
			(n.children ?? []).forEach(walk);
		};
		items.forEach(walk);

		let best: LabelLeaf | null = null;
		for (const c of all) {
			if (!c.path) continue;
			const matches = current === c.path || current.startsWith(c.path + '/');
			if (!matches) continue;
			if (!best || c.path.length > best.path.length) best = c;
		}
		return best?.label ?? sectionLabel ?? 'Secciones';
	}, [items, current, sectionLabel]);

	if (!sectionLabel) return null;

	const hasChildren = (sectionChildren?.length ?? 0) > 0;

	return (
		<section className={[CN, className].filter(Boolean).join(' ')}>
			{open && <div className={`${CN}__backdrop`} aria-hidden="true" onClick={() => setOpen(false)} />}
			<div className={`${CN}__bar`}>
				<div className={`${CN}__container`}>
					<span className={`${CN}__title`} title={currentLeafLabel}>
						{currentLeafLabel}
					</span>

					{hasChildren && (
						<button
							type="button"
							className={`${CN}__toggler`}
							aria-expanded={open}
							aria-controls={`${CN}__root`}
							onClick={() => setOpen((v) => !v)}
						>
							<CTNIcon icon={open ? 'arrowUp' : 'arrowDown'} />
						</button>
					)}
				</div>
			</div>

			{/* Solo la sección activa y, si tiene, sus hijos */}
			{hasChildren && (
				<div id={`${CN}__root`} className={`${CN}__root`} hidden={!open}>
					<nav className={`${CN}__nav`} aria-label={`Navegación de ${sectionLabel} (móvil)`}>
						<ul className={`${CN}__list`} role="list">
							{sectionChildren.map((ch) => (
								<li key={`${ch.label}-${ch.to ?? 'nolink'}`} className={`${CN}__item`}>
									<NavLink
										to={ch.to ?? '#'}
										className={({ isActive }) => `${CN}__link${isActive ? ' is-active' : ''}`}
										onMouseEnter={() => ch.to && prefetch(ch.to)}
									>
										<span className={`${CN}__label ${CN}__label--leaf`}>{ch.label}</span>
									</NavLink>
								</li>
							))}
						</ul>
					</nav>
				</div>
			)}
		</section>
	);
};
